import { useAuthActions } from "@convex-dev/auth/react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useAction, useConvexAuth, useQuery } from "convex/react";
import { useEffect, useRef, type ReactNode } from "react";

import { api } from "@convex/_generated/api";
import { SignupCodeInterstitial } from "@/components/signup/SignupCodeInterstitial";
import { Spinner } from "@/components/ui/Spinner";

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { signOut } = useAuthActions();
  const navigate = useNavigate();
  const user = useQuery(
    api.users.getMe,
    isAuthenticated ? {} : "skip",
  );
  const policy = useQuery(api.signupCodes.checkSignupPolicy);
  const redeemCode = useAction(api.signupCodes.redeemSignupCode);
  const signingOut = useRef(false);
  const redeemingRef = useRef(false);

  // Don't redirect while a code exchange is in progress
  const hasAuthCode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("code");

  // Only sign out if the user query has definitively returned null
  // (not undefined/loading) and we've been authenticated for a while
  const authStableRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        authStableRef.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    }
    authStableRef.current = false;
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      isAuthenticated &&
      authStableRef.current &&
      user === null &&
      !signingOut.current
    ) {
      signingOut.current = true;
      void signOut();
    }
  }, [isAuthenticated, user, signOut]);

  // Auto-redeem pending code from OAuth state parameter (INV-12)
  useEffect(() => {
    if (!isAuthenticated || !user || redeemingRef.current) return;
    if (user.signupCodeId !== undefined) return;

    const url = new URL(window.location.href);
    const pendingCode = url.searchParams.get("pendingCode");
    if (!pendingCode) return;

    redeemingRef.current = true;

    redeemCode({ code: pendingCode })
      .then(() => {
        // Clean up URL regardless of success — the interstitial handles retries
        url.searchParams.delete("pendingCode");
        window.history.replaceState({}, "", url.toString());
      })
      .catch(() => {
        url.searchParams.delete("pendingCode");
        window.history.replaceState({}, "", url.toString());
      })
      .finally(() => {
        redeemingRef.current = false;
      });
  }, [isAuthenticated, user, redeemCode]);

  if (isLoading || hasAuthCode) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" search={{ pendingCode: undefined }} />;
  }

  if (user === undefined || policy === undefined) {
    return <Spinner />;
  }

  // Route guard: un-activated user under INVITE_CODE policy → interstitial
  if (
    policy.mode === "INVITE_CODE" &&
    user &&
    user.signupCodeId === undefined
  ) {
    return <SignupCodeInterstitial onGoBack={() => void navigate({ to: "/" })} />;
  }

  return <>{children}</>;
}
